/**
 * BlogDetailPage - Standalone page for viewing blog content details
 *
 * Layout: a persistent header (breadcrumb, title, keyword chips, actions,
 * slim status pill row) above page-level tabs:
 *   Progress · Activity log · Generation internals (expert-only) · Details & QA
 *
 * Tab panels span the full content width — structural cards, tables, and
 * grids use the room; only prose (meta description) keeps a readable
 * max line length.
 *
 * The content-detail and generation-log polling hooks stay mounted at page
 * level so the header status pills update no matter which tab is open; only
 * the active tab's content mounts (Radix Tabs default). The expert timeline
 * fetches lazily by virtue of its tab content only mounting when active.
 *
 * Default tab is computed ONCE from the first-seen content status
 * ('Details & QA' when completed, 'Progress' otherwise) — the page never
 * auto-switches tabs on status transitions mid-session.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Breadcrumb, Button, Alert, message, Tag, Card, Tooltip } from 'antd';
import { AxiosError } from 'axios';
import {
  Home,
  FileText,
  Rocket,
  ExternalLink,
  Pencil,
  Info,
  RotateCcw,
  ClipboardCheck,
  UploadCloud,
  Check,
  Circle,
  Loader2,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FixingOverlay } from '@/components/ui/FixingLoader';
import { useContentDetail, usePublishContent, useRegenerateContentStage, useGenerationLog } from '@/hooks/useBlogCreationAPI';
import { useGenerationTimeline } from '@/hooks/useGenerationTimeline';
import { useQAReview } from '@/hooks/useQAReviews';
import { getIssueCount } from '@/types/qaReview';
import { useBlogTemplate } from '@/hooks/useBlogTemplates';
import GenerationActivityLog from '@/components/BlogCreationCommandCenter/GenerationActivityLog';
import { GENERATION_ERROR_MESSAGE } from '@/components/BlogCreationCommandCenter/generationErrorCopy';
import ExpertGenerationTimeline from '@/components/BlogCreationCommandCenter/ExpertGenerationTimeline';
import StatStrip, { type StatItem } from '@/components/BlogCreationCommandCenter/StatStrip';
import { canViewGenerationInternals } from '@/lib/generationInternalsAccess';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { formatStageLabel } from '@/lib/formatStageLabel';
import { generateHTMLFromRTE, applyTemplateStylesToContent } from '@/components/blog-templates';
import { blogCreationService } from '@/services/blogCreationService';
import { isPublishCancelledError } from '@/lib/prePublishGate';
import { KeywordMetricsHeader, type KeywordWithMetrics } from '@/components/editor-panels';
import { QAPublishWarning, useQAPublishGate } from '@/components/qa/QAPublishWarning';
import JudgeReportCard from '@/components/BlogCreationCommandCenter/panels/JudgeReportCard';
import StructureCard from '@/components/BlogCreationCommandCenter/panels/StructureCard';
import { extractArticleShape } from '@/components/BlogCreationCommandCenter/articleShape';
import { useTenantStore } from '@/stores/useTenantStore';
import type { BlockProgressStatus } from '@/components/BlogCreationCommandCenter/types';
import loadingAnimation from '@/assets/loading-animation.webp';

/** Shared underline-style tab trigger (matches the Generation internals tabs) */
const TAB_TRIGGER_CLASS =
  'gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm text-muted-foreground ' +
  'data-[state=active]:border-coral data-[state=active]:bg-transparent ' +
  'data-[state=active]:text-foreground data-[state=active]:shadow-none';

/** Block-row visuals for the Progress checklist grid (theme tokens) */
const BLOCK_STATUS_STYLES: Record<
  BlockProgressStatus,
  { icon: React.ReactNode; textClass: string; bgClass: string }
> = {
  pending: {
    icon: <Circle size={14} className="text-muted-foreground" />,
    textClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
  },
  in_progress: {
    icon: <Loader2 size={14} className="text-coral animate-spin" />,
    textClass: 'text-coral-dark font-medium',
    bgClass: 'bg-coral-light',
  },
  completed: {
    icon: <Check size={14} className="text-teal-dark" />,
    textClass: 'text-teal-dark',
    bgClass: 'bg-teal-light/60',
  },
};

/** "12m 34s" / "45s" / "1h 5m" from milliseconds */
function formatElapsedMs(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const BlogDetailPage: React.FC = () => {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();
  const { currentTenant } = useTenantStore();
  const tenantId = currentTenant || '';

  // Track HTML source for user feedback
  const [lastHtmlSource, setLastHtmlSource] = useState<'content_json' | 'content_html_fallback' | null>(null);

  // Fetch content details (polls while generating — drives the header pills)
  const { content, isLoading, refetch } = useContentDetail(contentId || '');
  const publishMutation = usePublishContent();
  const { mutateAsync: regenerateContent, isPending: isRegenerating } = useRegenerateContentStage();

  // Fetch template data when content has a template_id
  const { data: templateData } = useBlogTemplate(content?.template_id || null);

  // QA review data
  const { data: qaReview } = useQAReview(contentId || '', tenantId);

  // Article-shape verdict for the Structure card. The gate is non-blocking, so
  // an article can ship while failing it — this is the only surface that says
  // so. It rides along on the content detail response; null = never checked.
  const articleShape = useMemo(
    () => extractArticleShape(content?.generation_metadata),
    [content?.generation_metadata],
  );

  // QA publish gate - wraps handlePublish with warning dialog
  const { triggerPublish, warningProps } = useQAPublishGate(
    contentId || '',
    tenantId,
    () => handlePublish(),
  );

  // Get block progress from stored data (React Query polling handles updates)
  const blocksProgress = content?.blocks_progress || null;

  // CR-2: Generation activity log with pipeline scores. The hook stays
  // mounted at page level (it polls while generating); only the RENDER is
  // gated by the Activity log tab being active.
  const isGenerating = content?.status === 'in_progress';
  const { data: generationLogData } = useGenerationLog(contentId || null, {
    enabled: isGenerating || content?.status === 'completed' || content?.status === 'failed',
  });

  // Expert-only generation internals (attempt timeline + per-step feedback).
  // The tab does not exist for anyone else — absent, not disabled.
  const userProfile = useWorkspaceStore((state) => state.userProfile);
  const showGenerationInternals = canViewGenerationInternals(userProfile);

  // Attempt count for the Progress stat strip — experts only (the timeline
  // endpoint 403s for everyone else). The query key is shared with the
  // internals tab, so this costs at most one fetch per article.
  const { data: timelineData } = useGenerationTimeline(contentId || null, {
    enabled: showGenerationInternals,
  });

  // Latest pipeline quality score from the generation log (composite first,
  // most recent stage score as fallback) — same derivation as the activity log.
  const qualityScore = useMemo(() => {
    const entries = generationLogData?.generation_log ?? [];
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].step === 'pipeline_composite' && entries[i].score != null) {
        return entries[i].score as number;
      }
    }
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].score != null) return entries[i].score as number;
    }
    return null;
  }, [generationLogData]);

  // Default tab: computed ONCE from the first-seen status, then never
  // auto-switched — a mid-session in_progress→completed transition must not
  // yank the user off the tab they're reading.
  const [userTab, setUserTab] = useState<string | null>(null);
  const initialTabRef = useRef<string | null>(null);
  if (initialTabRef.current === null && content) {
    initialTabRef.current = content.status === 'completed' ? 'details' : 'progress';
  }
  const activeTab = userTab ?? initialTabRef.current ?? 'progress';

  /**
   * Handle publish to WordPress
   *
   * Flow:
   * 1. If content_json exists:
   *    a. Apply template styles to content (if template available)
   *    b. Generate fresh HTML using frontend's authoritative converter
   * 2. Update the content_html in the backend to ensure it's in sync
   * 3. Publish to WordPress
   * 4. Show user which HTML source was used
   */
  const handlePublish = async () => {
    if (!contentId || !content) return;

    try {
      // Step 1: Generate fresh HTML from content_json if available
      if (content.content_json) {
        // Step 1a: Apply template styles to content if template is available
        const styledContent = applyTemplateStylesToContent(
          content.content_json,
          templateData?.builder_blocks || []
        );

        // Step 1b: Generate HTML from styled content
        const freshHtml = styledContent
          ? generateHTMLFromRTE(styledContent, currentTenant)
          : generateHTMLFromRTE(content.content_json, currentTenant);

        // Step 2: Update backend with fresh HTML before publishing
        // This ensures content_html is in sync with content_json
        await blogCreationService.updateContent(contentId, freshHtml);

        // Track that we used content_json for user feedback
        setLastHtmlSource('content_json');
      } else {
        // Fallback: Will use existing content_html
        setLastHtmlSource('content_html_fallback');
      }

      // Step 3: Publish to WordPress
      const publishResult = await publishMutation.mutateAsync(contentId);

      // Step 4: Show success with source information
      const sourceLabel = content.content_json
        ? templateData?.builder_blocks?.length
          ? 'Fresh HTML generated with template styles applied'
          : 'Fresh HTML generated from editor content'
        : 'Using stored HTML (no editor content available)';

      message.success({
        content: `Published to WordPress! ${sourceLabel}`,
        duration: 5,
      });

      refetch();
    } catch (err) {
      // User chose "Go back" in the pre-publish metadata gate — abort quietly.
      if (isPublishCancelledError(err)) return;
      // Show specific error message based on backend response
      if (err instanceof AxiosError && err.response?.status === 400) {
        const detail = (err.response.data as { detail?: string | { error?: string; errors?: string[] } })?.detail;
        if (typeof detail === 'object' && detail?.errors && detail.errors.length > 0) {
          // SEO validation error - show first error with longer duration
          message.error(`SEO Error: ${detail.errors[0]}`, 8);
        } else if (typeof detail === 'string') {
          // Simple string error (content not found, access denied, wrong status)
          message.error(detail);
        } else {
          message.error('Failed to publish to WordPress');
        }
      } else {
        message.error('Failed to publish to WordPress');
      }

      setLastHtmlSource(null);
    }
  };

  /**
   * Handle retry content generation
   * Regenerates the entire content pipeline from scratch
   */
  const handleRetry = async () => {
    if (!contentId) return;

    try {
      message.loading({ content: 'Retrying content generation...', key: 'retry' });
      await regenerateContent({ contentId, stage: 'all' });
      message.success({ content: 'Content generation restarted!', key: 'retry' });
      refetch();
    } catch (err) {
      console.error('Failed to retry content generation:', err);
      message.error({
        content: `Failed to retry: ${err instanceof Error ? err.message : 'Unknown error'}`,
        key: 'retry'
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
      </div>
    );
  }

  // Error state
  if (!content) {
    return (
      <div className="h-screen flex items-center justify-center bg-muted p-4">
        <Alert
          message="Content not found"
          description="The requested content could not be loaded."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate('/content/planner')}>
              Back to Content Planner
            </Button>
          }
        />
      </div>
    );
  }

  // Status helpers. A WP post id alone doesn't mean live — the post may exist
  // as a WP draft (wordpress_status='draft'). "Published" only when WP says
  // 'publish' (same derivation as ContentQueueTable).
  const canPublish = content.status === 'completed' && !content.wordpress_post_id;
  const hasWordPressPost = !!content.wordpress_post_id;
  const isPublished = hasWordPressPost && content.wordpress_status === 'publish';
  const isWordPressDraft = hasWordPressPost && !isPublished;

  const statusStyles: Record<string, { label: string; pill: string }> = {
    completed: { label: 'Completed', pill: 'bg-teal-light text-teal-dark' },
    in_progress: { label: 'In Progress', pill: 'bg-coral-light text-coral-dark' },
    failed: { label: 'Failed', pill: 'bg-destructive/10 text-destructive' },
    default: { label: 'Pending', pill: 'bg-muted text-muted-foreground' },
  };

  const currentStatusStyle = statusStyles[content.status] || statusStyles.default;
  const showProgressLine =
    content.progress_percentage !== undefined && !['completed', 'failed'].includes(content.status);
  const secondaryKeywords = content.secondary_keywords || [];
  const visibleSecondaryKeywords = secondaryKeywords.slice(0, 6);
  const remainingKeywords = Math.max(secondaryKeywords.length - visibleSecondaryKeywords.length, 0);
  const showKeywordMetricsHeader = Boolean(content.keyword);
  const showSecondaryKeywordsInDetails = secondaryKeywords.length > 0 && !showKeywordMetricsHeader;

  // ---------------------------------------------------------------------
  // Progress tab derivations
  // ---------------------------------------------------------------------
  const sortedBlocks = [...(blocksProgress ?? [])].sort((a, b) => a.order - b.order);
  const completedBlocks = sortedBlocks.filter((b) => b.status === 'completed').length;
  const inProgressBlock = sortedBlocks.find((b) => b.status === 'in_progress');

  // Trust fix: a fresh attempt (pending, or in_progress at 0%) with completed
  // items left over from a previous attempt must not show confident green.
  const checklistStale =
    completedBlocks > 0 &&
    (content.status === 'pending' ||
      (content.status === 'in_progress' && (content.progress_percentage ?? 0) === 0));

  const logEntries = generationLogData?.generation_log ?? [];
  const elapsedMs = logEntries.length > 0 ? logEntries[logEntries.length - 1].elapsed_ms : null;
  const attemptCount = timelineData?.attempts?.length ?? 0;

  const progressStats: StatItem[] = [
    elapsedMs != null ? { key: 'elapsed', label: 'Elapsed', value: formatElapsedMs(elapsedMs) } : null,
    // Experts only — regular users have no cheap attempts source, so omitted
    attemptCount > 0 ? { key: 'attempt', label: 'Attempt', value: attemptCount } : null,
    sortedBlocks.length > 0
      ? { key: 'blocks', label: 'Blocks done', value: completedBlocks, hint: `of ${sortedBlocks.length}` }
      : null,
    qualityScore != null
      ? { key: 'score', label: 'Quality score', value: qualityScore, hint: '/100' }
      : null,
  ].filter(Boolean) as StatItem[];

  const nowWritingLabel =
    inProgressBlock?.label || formatStageLabel(content.current_stage) || content.current_stage;

  // ---------------------------------------------------------------------
  // Details & QA tab derivations
  // ---------------------------------------------------------------------
  const detailStats: StatItem[] = [
    {
      key: 'keyword',
      label: 'Keyword',
      value: <Tag className="text-sm px-2 m-0">{content.keyword}</Tag>,
    },
    showSecondaryKeywordsInDetails
      ? {
          key: 'secondary',
          label: 'Secondary keywords',
          value: (
            <div className="flex flex-wrap gap-1">
              {visibleSecondaryKeywords.map((kw, i) => (
                <Tag key={`${kw}-${i}`} className="text-xs px-2 py-0.5 m-0">
                  {kw}
                </Tag>
              ))}
              {remainingKeywords > 0 && (
                <Tag className="text-xs px-2 py-0.5 m-0">+{remainingKeywords} more</Tag>
              )}
            </div>
          ),
        }
      : null,
    {
      key: 'created',
      label: 'Created',
      value: new Date(content.created_at).toLocaleString(),
    },
    content.completed_at
      ? {
          key: 'completed',
          label: 'Completed',
          value: new Date(content.completed_at).toLocaleString(),
        }
      : null,
    content.total_word_count
      ? {
          key: 'wordCount',
          label: 'Word count',
          value: content.total_word_count.toLocaleString(),
          hint: 'words',
        }
      : null,
  ].filter(Boolean) as StatItem[];

  return (
    <div className="h-screen flex flex-col bg-muted">
      {/* Persistent header — visible above the tabs on every tab */}
      <div className="relative bg-card border-b border-border px-4 pt-3 pb-2">
        <FixingOverlay
          show={publishMutation.isPending}
          label="Pushing to WordPress…"
          icon={UploadCloud}
        />
        <div className="flex items-center justify-between mb-2">
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to="/content/planner" className="flex items-center gap-1 hover:text-blue-500">
                    <Home size={14} />
                    <span>Content Planner</span>
                  </Link>
                ),
              },
              {
                title: (
                  <span className="flex items-center gap-1 text-foreground font-medium">
                    <FileText size={14} />
                    <span>{content.keyword || 'Untitled'}</span>
                  </span>
                ),
              },
            ]}
          />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {content.content_json && (
              <Button
                icon={<Pencil size={14} />}
                onClick={() => navigate(`/editor/blog/${contentId}`)}
              >
                Edit Content
              </Button>
            )}

            {canPublish && (
              <Tooltip
                title={
                  content.content_json
                    ? 'Will generate fresh HTML from editor content'
                    : 'Will use stored HTML (edit content first to enable fresh HTML generation)'
                }
              >
                <Button
                  type="primary"
                  icon={<Rocket size={14} />}
                  onClick={triggerPublish}
                  loading={publishMutation.isPending}
                >
                  Push to WordPress
                  {content.content_json && (
                    <Info size={12} className="ml-1 opacity-70" />
                  )}
                </Button>
              </Tooltip>
            )}

            {hasWordPressPost && content.wordpress_post_url && (
              <Button
                type="primary"
                icon={<ExternalLink size={14} />}
                href={content.wordpress_post_url}
                target="_blank"
              >
                View in WordPress
              </Button>
            )}
          </div>
        </div>

        <h1 className="text-xl font-semibold text-foreground">
          {content.title || content.keyword || 'Untitled'}
        </h1>

        {/* Slim status pill row — updates on every tab (polling stays mounted) */}
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium ${currentStatusStyle.pill}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {currentStatusStyle.label}
          </span>
          {showProgressLine && (
            <span className="text-muted-foreground">
              {content.progress_percentage}% · {formatStageLabel(content.current_stage) || content.current_stage}
            </span>
          )}
          {isPublished && (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium bg-teal-light text-teal-dark">
              <Rocket size={11} />
              Published to WordPress
            </span>
          )}
          {isWordPressDraft && (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium bg-muted text-muted-foreground">
              <FileText size={11} />
              Draft on WordPress
            </span>
          )}
        </div>

        {/* Keyword chips + add-keyword */}
        {showKeywordMetricsHeader && (
          <KeywordMetricsHeader
            className="mt-2 border-b-0"
            primaryKeyword={{
              keyword: content.keyword,
              // Use target_keywords if available for metrics
              search_volume: content.target_keywords?.find(k => k.is_primary)?.search_volume,
              keyword_difficulty: content.target_keywords?.find(k => k.is_primary)?.keyword_difficulty,
              cpc: content.target_keywords?.find(k => k.is_primary)?.cpc,
              is_primary: true,
            }}
            secondaryKeywords={
              secondaryKeywords.map((kw) => {
                // Try to find metrics from target_keywords
                const metrics = content.target_keywords?.find(
                  t => t.keyword.toLowerCase() === kw.toLowerCase()
                );
                return {
                  keyword: kw,
                  search_volume: metrics?.search_volume,
                  keyword_difficulty: metrics?.keyword_difficulty,
                  cpc: metrics?.cpc,
                  is_primary: false,
                } as KeywordWithMetrics;
              })
            }
          />
        )}
      </div>

      {/* Page-level tabs. Only the active tab's content mounts. */}
      <Tabs
        value={activeTab}
        onValueChange={setUserTab}
        className="flex-1 flex flex-col min-h-0"
      >
        {/* The tab list scrolls itself on narrow widths — never the page */}
        <div className="bg-card border-b border-border px-4 overflow-x-auto">
          <TabsList className="h-auto w-max justify-start gap-0 rounded-none bg-transparent p-0">
            <TabsTrigger value="progress" className={TAB_TRIGGER_CLASS}>
              Progress
              {isGenerating && (
                <span
                  aria-hidden
                  data-testid="progress-live-dot"
                  className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse flex-shrink-0"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className={TAB_TRIGGER_CLASS}>
              Activity log
            </TabsTrigger>
            {/* Expert-only: absent (not disabled) for everyone else */}
            {showGenerationInternals && (
              <TabsTrigger value="internals" className={TAB_TRIGGER_CLASS}>
                Generation internals
              </TabsTrigger>
            )}
            <TabsTrigger value="details" className={TAB_TRIGGER_CLASS}>
              Details & QA
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Full-width tab panels — no max-width wrapper */}
        <div className="flex-1 overflow-auto p-4">
          {/* Progress: stat strip + now-writing callout + block grid (+ error/retry) */}
          <TabsContent value="progress" className="mt-0 space-y-4">
            {progressStats.length > 0 && <StatStrip items={progressStats} />}

            {isGenerating && nowWritingLabel && (
              <div className="flex items-center gap-2 rounded-lg border border-coral/30 bg-coral-light px-4 py-2.5 text-sm text-coral-dark">
                <span className="w-2 h-2 rounded-full bg-coral animate-pulse flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">Now writing:</span>
                <span className="truncate">{nowWritingLabel}</span>
              </div>
            )}

            <Card
              size="small"
              className="border border-border shadow-sm"
              bodyStyle={{ padding: 16 }}
            >
              {sortedBlocks.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground mb-2">
                    <span className="font-medium">Block progress</span>
                    <span className="flex items-center gap-2">
                      {checklistStale && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          From the previous attempt
                        </span>
                      )}
                      <span className="tabular-nums">
                        {completedBlocks} of {sortedBlocks.length} complete
                      </span>
                    </span>
                  </div>
                  <div className={checklistStale ? 'opacity-60' : undefined}>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full bg-teal transition-all duration-300 ease-out"
                        style={{ width: `${(completedBlocks / sortedBlocks.length) * 100}%` }}
                      />
                    </div>
                    {/* Responsive grid, labels untruncated (blocks carry no
                        section grouping — flat list) */}
                    <div className="grid grid-cols-1 gap-1.5 min-[1024px]:grid-cols-2 min-[1440px]:grid-cols-3">
                      {sortedBlocks.map((block) => {
                        const style = BLOCK_STATUS_STYLES[block.status] ?? BLOCK_STATUS_STYLES.pending;
                        return (
                          <div
                            key={block.block_id}
                            className={`flex items-start gap-2 rounded px-2.5 py-1.5 text-sm ${style.bgClass}`}
                          >
                            <span className="flex-shrink-0 mt-0.5">{style.icon}</span>
                            <span className={`min-w-0 break-words ${style.textClass}`}>
                              {block.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No block progress recorded for this article yet.
                </div>
              )}
            </Card>

            {/* Error Message with Retry Button. content.error_message may
                still hold raw exception text from before the backend fix
                (cost/token/exception-class leak) — render fixed copy rather
                than the stored value; see generationErrorCopy.ts. */}
            {content.error_message && (
              <Alert
                message="Generation Error"
                description={
                  <div className="space-y-3">
                    <p>{GENERATION_ERROR_MESSAGE}</p>
                    <Button
                      type="primary"
                      danger
                      icon={<RotateCcw size={14} />}
                      onClick={handleRetry}
                      loading={isRegenerating}
                    >
                      Retry Generation
                    </Button>
                  </div>
                }
                type="error"
                showIcon
              />
            )}
          </TabsContent>

          {/* Activity log: the tab is the disclosure — embedded mode drops the
              component's own collapse header. */}
          <TabsContent value="activity" className="mt-0">
            <Card
              size="small"
              className="border border-border shadow-sm"
              bodyStyle={{ padding: 0 }}
            >
              {generationLogData && generationLogData.generation_log.length > 0 ? (
                <GenerationActivityLog
                  entries={generationLogData.generation_log}
                  startedAt={generationLogData.generation_started_at}
                  isGenerating={isGenerating}
                  status={content.status}
                  errorMessage={content.error_message}
                  embedded
                />
              ) : (
                <div className="text-sm text-muted-foreground px-4 py-3">
                  No activity has been recorded for this article yet.
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Expert-only: generation internals. Mounts (and fetches) only
              when this tab is active — laziness via tab gating. */}
          {showGenerationInternals && (
            <TabsContent value="internals" className="mt-0">
              {contentId && <ExpertGenerationTimeline contentId={contentId} />}
            </TabsContent>
          )}

          {/* Details & QA: stat row + meta description + QA card grid */}
          <TabsContent value="details" className="mt-0 space-y-4">
            <StatStrip items={detailStats} />

            {content.meta_description && (
              <Card
                size="small"
                className="border border-border shadow-sm"
                bodyStyle={{ padding: 16 }}
              >
                <div className="flex items-start gap-2">
                  <FileText size={16} className="text-teal-dark mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                      Meta Description
                    </div>
                    {/* Prose keeps a readable line length even on wide screens */}
                    <p className="mt-1 text-sm leading-relaxed text-foreground max-w-prose">
                      {content.meta_description}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {content.status === 'completed' && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 items-start">
                {/* QA Review Summary */}
                <Card
                  size="small"
                  className="border border-border shadow-sm"
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck size={16} className="text-muted-foreground" />
                      <span className="text-sm font-semibold text-foreground">QA Review</span>
                    </div>
                    {qaReview?.reviewed_at ? (
                      (() => {
                        const issueCount = getIssueCount(qaReview);
                        return issueCount === 0 ? (
                          <Tag color="success" className="m-0">Clean</Tag>
                        ) : (
                          <Tag color="error" className="m-0">
                            {issueCount} issue{issueCount !== 1 ? 's' : ''} found
                          </Tag>
                        );
                      })()
                    ) : (
                      <Tag color="default" className="m-0">Not reviewed</Tag>
                    )}
                  </div>
                  <div className="mt-2">
                    <Link
                      to={`/content/qa`}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      View full QA checklist
                    </Link>
                  </div>
                </Card>

                {/* Article shape vs the platform minimums (non-blocking gate) */}
                <StructureCard shape={articleShape} />

                {/* Content Judge Report (rule checks + LLM critique) */}
                {contentId && <JudgeReportCard contentId={contentId} />}
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>

      {/* QA Publish Warning Dialog */}
      {contentId && <QAPublishWarning {...warningProps} />}
    </div>
  );
};

export default BlogDetailPage;
